import { useCallback } from "react";

import { ChevronRight } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useAnalytics, { AnalyticsCommonProps } from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { ThemeContext } from "views/Theme";

import { CardCollapsibleContext } from "./Context";
import styles from "./styles.module.css";
import { CardCollapsibleAriaLevel } from "./types";

export type CardCollapsibleToggleProps = AnalyticsCommonProps & {
  ariaLevel: CardCollapsibleAriaLevel;
} & ({ ariaLabel: string; ariaLabelledby?: never } | { ariaLabel?: never; ariaLabelledby: string });

function CardCollapsibleToggle({
  analyticsPage,
  analyticsTitle,
  analyticsProps,
  ariaLevel,
  ariaLabelledby,
  ariaLabel,
}: CardCollapsibleToggleProps) {
  const { onToggle, isCollapsed, contentId } = useTypedContext(CardCollapsibleContext);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const trackAnalytics = useCallback(() => {
    if (analyticsTitle) {
      trackSegmentAnalyticsEvent?.(analyticsTitle, { ...analyticsProps, visible: !isCollapsed });
    }
  }, [analyticsTitle, analyticsProps, trackSegmentAnalyticsEvent, isCollapsed]);

  const handleClickToggle = useCallback(() => {
    onToggle();
    trackAnalytics();
  }, [onToggle, trackAnalytics]);

  const { reducedMotion } = useTypedContext(ThemeContext);

  return (
    <Box
      align="center"
      role="heading"
      aria-level={ariaLevel}
      className={styles.collapsibleToggleWrapper}
    >
      <ButtonIcon
        onPress={handleClickToggle}
        variant="ghost"
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
        icon={ChevronRight}
        iconRotate={isCollapsed ? undefined : "90"}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        className={!reducedMotion ? styles.collapsibleToggle : undefined}
      >
        {isCollapsed ? "Expand" : "Collapse"}
      </ButtonIcon>
    </Box>
  );
}

CardCollapsibleToggle.displayName = "DS.Card.Collapsible.Toggle";
export default CardCollapsibleToggle;
